import { useEffect, useState } from "react";
import { admin, type HostBudget, type JobView, type Schedule } from "../../api/admin";
import { when } from "./bits";

export function Settings() {
  return (
    <>
      <Schedules />
      <Hosts />
    </>
  );
}

// ---------------------------------------------------------------------------

function Schedules() {
  const [rows, setRows] = useState<Schedule[] | null>(null);
  const [jobs, setJobs] = useState<JobView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState({ kind: "", cron: "30 3 * * *" });

  const load = () => {
    admin.schedules().then(setRows).catch((e: Error) => setError(e.message));
    admin.jobs().then(setJobs).catch(() => { /* the list is a convenience */ });
  };
  useEffect(load, []);

  const act = async (fn: () => Promise<unknown>) => {
    setError(null);
    try { await fn(); load(); } catch (e) { setError((e as Error).message); }
  };

  return (
    <section className="panel-section">
      <h2>Schedules</h2>
      <p className="dim small">
        Five-field cron, in UTC. A firing whose previous run is still going is
        skipped rather than queued behind it — a crawl that overruns its own
        schedule should not build a backlog of crawls.
      </p>

      {error && <div className="error">{error}</div>}

      <table className="runs">
        <thead>
          <tr><th>job</th><th>cron</th><th>next</th><th>last</th><th>on</th><th /></tr>
        </thead>
        <tbody>
          {rows?.map((s) => (
            <tr key={s.id}>
              <td><code>{s.kind}</code></td>
              <td><code>{s.cron}</code></td>
              <td className="dim">{s.enabled ? when(s.nextRunAt) : "—"}</td>
              <td className="dim">{when(s.lastRunAt)}</td>
              <td>
                <input
                  type="checkbox"
                  checked={s.enabled}
                  onChange={(e) =>
                    act(() => admin.updateSchedule(s.id, {
                      kind: s.kind, cron: s.cron, params: s.params,
                      enabled: e.target.checked,
                    }))
                  }
                />
              </td>
              <td>
                <button className="link" onClick={() => act(() => admin.deleteSchedule(s.id))}>
                  remove
                </button>
              </td>
            </tr>
          ))}
          {rows?.length === 0 && (
            <tr><td colSpan={6} className="dim">Nothing is scheduled.</td></tr>
          )}
        </tbody>
      </table>

      <div className="addrow">
        <select value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value })}>
          <option value="">Pick a job…</option>
          {jobs.map((j) => (
            <option key={j.kind} value={j.kind} disabled={!j.available}>
              {j.title}{j.available ? "" : " (unavailable)"}
            </option>
          ))}
        </select>
        <input
          value={draft.cron}
          onChange={(e) => setDraft({ ...draft, cron: e.target.value })}
          placeholder="minute hour day month weekday"
        />
        <button
          disabled={!draft.kind}
          onClick={() =>
            act(async () => {
              // No parameters of its own: the schedule runs on the job's saved
              // defaults, so there is one place to change what it does and it
              // is the same place a one-click run reads.
              await admin.createSchedule({ kind: draft.kind, cron: draft.cron, params: {} });
              setDraft({ kind: "", cron: "30 3 * * *" });
            })
          }
        >
          Add
        </button>
        <span className="dim small">runs with that job&rsquo;s saved defaults</span>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------

function Hosts() {
  const [rows, setRows] = useState<HostBudget[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [edit, setEdit] = useState<Record<string, string>>({});

  useEffect(() => {
    admin.hosts().then(setRows).catch((e: Error) => setError(e.message));
  }, []);

  const save = (host: string) => {
    const v = Number(edit[host]);
    if (!v || v <= 0) return;
    admin.setHost(host, v)
      .then((next) => { setRows(next); setEdit((e) => ({ ...e, [host]: "" })); })
      .catch((e: Error) => setError(e.message));
  };

  return (
    <section className="panel-section">
      <h2>Rate budgets</h2>
      <p className="dim small">
        Requests a minute, per remote host, shared by every running job and by
        the API&rsquo;s own preview fetches. This is what replaced the old
        per-stage delay flags: four stages all hitting Bandcamp is four times
        the request rate at one host, whatever each one&rsquo;s own delay says.
      </p>

      {error && <div className="error">{error}</div>}

      <table className="runs">
        <thead><tr><th>host</th><th>per minute</th><th>why</th><th /></tr></thead>
        <tbody>
          {rows?.map((b) => (
            <tr key={b.host}>
              <td><code>{b.host}</code></td>
              <td className="num">{b.perMinute.toLocaleString()}</td>
              <td className="dim small">
                {b.note}
                {b.hardFloor && <b className="floor"> · cannot be raised</b>}
              </td>
              <td>
                <input
                  type="number" min={1} max={6000} className="tiny"
                  placeholder="new"
                  value={edit[b.host] ?? ""}
                  onChange={(e) => setEdit({ ...edit, [b.host]: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && save(b.host)}
                />
                <button className="link" onClick={() => save(b.host)}>set</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
